import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Play, ArrowLeft, Terminal, Code, X, Edit2, Check, Download, Eye, EyeOff, Merge, CheckCircle, Search } from 'lucide-react';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import CodeEditor from './ui/CodeEditor';
import NetronVisualizer from './LayerOptimizer/NetronVisualizer';
import ExecutionResults from './ExecutionResults';
import { useExecutionSubmit, useExecutionJob } from '../hooks/useExecutionJob';
import { useAuth } from '../contexts/AuthContext';
import { useSession } from '../hooks/useSession';
import sessionApiService from '../services/sessionApi';
import { exportSessionToPython } from '../utils/pythonExport';
import UnifiedHeader from './UnifiedHeader';

const LayerOptimizer = ({
  selectedLayerId,
  modelLayers,  // Lightweight layer list (id, name, type only)
  onClose,
  onSaveOptimization,
  modelName,
  modelId,
  hardware
}) => {
  const [searchParams] = useSearchParams();
  const [currentLayerId, setCurrentLayerId] = useState(selectedLayerId);
  const [currentLayer, setCurrentLayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [contextVisible, setContextVisible] = useState(true);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [displayedLogsCount, setDisplayedLogsCount] = useState(0);
  const [metricsDisplayed, setMetricsDisplayed] = useState(false);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [waitingForSessionData, setWaitingForSessionData] = useState(false);
  const [showCreateSessionDialog, setShowCreateSessionDialog] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [renamingSession, setRenamingSession] = useState(false);
  const [sessionRenameValue, setSessionRenameValue] = useState('');
  const [exportingPython, setExportingPython] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedLayers, setSelectedLayers] = useState([]);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeFeedPositions, setMergeFeedPositions] = useState([0]);
  const [merging, setMerging] = useState(false);
  const [testingLayer, setTestingLayer] = useState(false);
  const [visualizerRefreshTrigger, setVisualizerRefreshTrigger] = useState(0);
  const [layerSearchQuery, setLayerSearchQuery] = useState('');
  const [layerSearchExpanded, setLayerSearchExpanded] = useState(false);
  const consoleRef = useRef(null);
  const editorRef = useRef(null);  // Direct reference to Monaco editor
  const lastJobStatusRef = useRef(null);  // Track last job status to avoid duplicate messages
  const layerListRef = useRef(null);  // Reference to scrollable layer list container
  const { session } = useAuth();

  // Session management with autosave
  const {
    currentSession,
    sessionLayers,
    hasRecoverableSessions,
    availableSessions,
    checkingForSessions,
    autosaving,
    autosaveLayer,
    flushAutosave,
    saveExecutionResult,
    getLayerCode,
    isLayerModified,
    createSession,
    loadSession,
    updateSession,
    loading: sessionLoading
  } = useSession(modelId, hardware);

  // Enhance model layers with session-specific layers (like merged layers)
  const enhancedLayers = React.useMemo(() => {
    console.log('🔍 EnhancedLayers computation:', {
      hasSession: !!currentSession,
      sessionLayersCount: Object.keys(sessionLayers || {}).length,
      modelLayersCount: modelLayers.length,
      sessionLayers: sessionLayers
    });

    if (!currentSession || !sessionLayers || Object.keys(sessionLayers).length === 0) {
      console.log('  → Returning original modelLayers (no session or empty sessionLayers)');
      return modelLayers;
    }

    // Start with model layers that aren't deleted in the session
    const deletedLayerIds = new Set();
    const mergedLayers = [];
    let helperLayer = null;

    Object.values(sessionLayers).forEach(sessionLayer => {
      console.log('  📄 Processing session layer:', {
        id: sessionLayer.id,
        layer_id: sessionLayer.layer_id,
        has_merge_metadata: !!sessionLayer.merge_metadata,
        is_merged: sessionLayer.merge_metadata?.is_merged,
        is_helper_OLD: sessionLayer.merge_metadata?.is_helper_functions,
        is_helper_NEW: sessionLayer.is_helper_functions,
        is_helper_type: typeof sessionLayer.is_helper_functions,
        deleted_at: sessionLayer.deleted_at,
        all_keys: Object.keys(sessionLayer)
      });

      // Check if this is the helper functions layer
      if (!sessionLayer.layer_id && sessionLayer.is_helper_functions) {
        console.log('    🔧 Found helper functions layer!');
        helperLayer = {
          id: sessionLayer.id,
          name: 'Helper Functions',
          type: 'Helper',
          is_helper_functions: true,
          is_read_only: false,
          session_layer: sessionLayer,
          last_execution_status: sessionLayer.last_execution_status,
          last_execution_metrics: sessionLayer.last_execution_metrics,
          last_execution_output: sessionLayer.last_execution_output
        };
      }
      // Check if this is a merged layer (layer_id is null)
      else if (!sessionLayer.layer_id && sessionLayer.merge_metadata?.is_merged) {
        console.log('    ✨ Found merged layer!');
        mergedLayers.push({
          id: sessionLayer.id, // Use session_layer.id for merged layers
          name: sessionLayer.merge_metadata.merged_function_name || 'Merged Layer',
          type: 'Merged',
          is_composite: true,
          is_merged: true,
          merge_metadata: sessionLayer.merge_metadata
        });

        // Don't mark source layers as deleted - keep them visible for verification
        // if (sessionLayer.merge_metadata.source_layer_ids) {
        //   sessionLayer.merge_metadata.source_layer_ids.forEach(id => deletedLayerIds.add(id));
        // }
      }
      // Don't filter out deleted layers - keep them visible for verification
      // else if (sessionLayer.deleted_at) {
      //   deletedLayerIds.add(sessionLayer.layer_id);
      // }
    });

    console.log('  🎯 Results:', {
      deletedLayerIds: Array.from(deletedLayerIds),
      mergedLayersCount: mergedLayers.length,
      mergedLayers: mergedLayers
    });

    // Filter out deleted layers and add session data to model layers
    const filteredModelLayers = modelLayers
      .filter(layer => !deletedLayerIds.has(layer.id))
      .map(layer => {
        // Find matching session layer by layer_id
        const sessionLayer = Object.values(sessionLayers).find(sl => sl.layer_id === layer.id);
        if (sessionLayer) {
          // Enrich layer with session execution data
          return {
            ...layer,
            session_layer: sessionLayer,
            last_execution_status: sessionLayer.last_execution_status,
            last_execution_metrics: sessionLayer.last_execution_metrics,
            last_execution_output: sessionLayer.last_execution_output
          };
        }
        return layer;
      });
    
    // Insert helper layer right after main
    const mainIndex = filteredModelLayers.findIndex(l => l.name === 'main');
    const result = [
      ...filteredModelLayers.slice(0, mainIndex + 1),
      ...(helperLayer ? [helperLayer] : []),
      ...filteredModelLayers.slice(mainIndex + 1),
      ...mergedLayers
    ];
    console.log('  ✅ Final enhanced layers:', result.length, 'with session data attached', {
      hasHelperLayer: !!helperLayer
    });
    return result;
  }, [modelLayers, sessionLayers, currentSession]);

  // Handle session initialization and selection
  useEffect(() => {
    const initializeSession = async () => {
      // Check if a specific session ID was passed via URL
      const sessionIdFromUrl = searchParams.get('sessionId');

      console.log('Session initialization check:', {
        sessionIdFromUrl,
        checkingForSessions,
        hasRecoverableSessions,
        availableSessionsCount: availableSessions.length,
        currentSession,
        sessionLoading
      });

      // Wait for session check to complete before proceeding
      if (checkingForSessions) {
        console.log('Still checking for sessions, waiting...');
        return;
      }

      // If URL has sessionId and we haven't loaded it yet, load that specific session
      if (sessionIdFromUrl && !currentSession && !sessionLoading) {
        console.log('Auto-loading session from URL:', sessionIdFromUrl);
        setWaitingForSessionData(true);
        try {
          await loadSession(sessionIdFromUrl);
          const loadMessage = {
            timestamp: new Date().toLocaleTimeString(),
            type: 'success',
            message: '✅ Loaded session from navigation'
          };
          setConsoleOutput(prev => [...prev, loadMessage]);
        } catch (err) {
          console.error('Failed to auto-load session:', err);
          setWaitingForSessionData(false);
        }
        return;
      }

      // If no sessionId in URL, this shouldn't happen with the new flow
      // Session should always be selected/created in OptimizeHub before navigating here
      if (!sessionIdFromUrl && !currentSession && !sessionLoading) {
        console.error('No sessionId in URL - session should be selected before navigation.');
        const errorMessage = {
          timestamp: new Date().toLocaleTimeString(),
          type: 'error',
          message: '❌ No session selected. Please go back and select or create a session.'
        };
        setConsoleOutput(prev => [...prev, errorMessage]);
        return;
      }
    };

    initializeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, checkingForSessions, currentSession, sessionLoading]);

  // Fetch layer details when layer ID changes
  useEffect(() => {
    const fetchLayerDetails = async () => {
      if (!currentLayerId || !session?.access_token) return;

      // Wait for session layers to be loaded before fetching if we have a session
      // This prevents race conditions and duplicate API calls
      const hasSessionLayersCache = Object.keys(sessionLayers || {}).length > 0;
      if (currentSession?.id && !hasSessionLayersCache) {
        console.log('⏳ Waiting for session layers cache to populate...');
        return;
      }

      console.log('📥 Fetching layer details:', {
        layerId: currentLayerId,
        hasSession: !!currentSession,
        hasSessionLayersCache,
        hasCachedLayer: !!sessionLayers[currentLayerId],
        previousLayer: currentLayer?.layer_name || currentLayer?.name
      });

      setLoading(true);
      try {
        // Check if this is the helper functions layer
        const helperLayer = Object.values(sessionLayers || {}).find(
          sl => sl.id === currentLayerId && sl.is_helper_functions
        );

        if (helperLayer) {
          // Helper functions layer - populate editor directly from session layer data
          console.log('🔧 Loading helper functions layer from session data:', {
            id: helperLayer.id,
            name: 'Helper Functions',
            hasCurrentCode: !!helperLayer.current_code,
            codeLength: helperLayer.current_code?.length || 0
          });

          const layerData = {
            id: helperLayer.id,
            name: 'Helper Functions',
            type: 'Helper',
            is_helper_functions: true,
            function_code: helperLayer.current_code,
            io_metadata: null,
            session_layer: helperLayer,
            last_execution_status: helperLayer.last_execution_status,
            last_execution_metrics: helperLayer.last_execution_metrics,
            last_execution_output: helperLayer.last_execution_output,
            last_execution_at: helperLayer.last_execution_at
          };

          setCurrentLayer(layerData);
          console.log('✅ Helper functions layer loaded:', {
            currentLayerId,
            hasExecutionResults: !!helperLayer.last_execution_status
          });
        } else {
          // Check if this is a merged layer (exists in sessionLayers with merge_metadata)
          const sessionLayer = Object.values(sessionLayers || {}).find(
            sl => sl.id === currentLayerId && sl.merge_metadata?.is_merged
          );

          if (sessionLayer) {
          // This is a merged layer - fetch from session API to get latest data including io_metadata
          const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
          const response = await fetch(`${API_BASE}/api/sessions/${currentSession.id}/layers/${currentLayerId}`, {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch session layer details');
          }

          const sessionLayerData = await response.json();
          
          console.log('📦 Loading merged layer from session API:', {
            id: sessionLayerData.id,
            name: sessionLayerData.merge_metadata?.merged_function_name,
            type: 'Merged',
            hasCurrentCode: !!sessionLayerData.current_code,
            hasIOMetadata: !!sessionLayerData.io_metadata,
            codeLength: sessionLayerData.current_code?.length || 0,
            codePreview: sessionLayerData.current_code ? sessionLayerData.current_code.substring(0, 100) : '(none)'
          });

          const layerData = {
            id: sessionLayerData.id,
            name: sessionLayerData.merge_metadata?.merged_function_name || 'Merged Layer',
            type: 'Merged',
            is_composite: true,
            is_merged: true,
            merge_metadata: sessionLayerData.merge_metadata,
            function_code: sessionLayerData.current_code, // Full merged program
            io_metadata: sessionLayerData.io_metadata, // IO metadata for merged function
            session_layer: sessionLayerData
          };

          setCurrentLayer(layerData);
          console.log('✅ Merged layer switch completed:', {
            currentLayerId,
            layerName: sessionLayerData.merge_metadata?.merged_function_name,
            hasExecutionResults: !!(sessionLayerData?.last_execution_status),
            executionStatus: sessionLayerData?.last_execution_status
          });
        } else {
          // Regular model layer - fetch from API
          const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
          const response = await fetch(`${API_BASE}/api/models/layers/${currentLayerId}`, {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch layer details');
          }

          const layerData = await response.json();
          
          // Get session layer data from cache (for execution results)
          // Cache is guaranteed to be populated due to guard at top of function
          let sessionLayerData = null;
          if (currentSession?.id && sessionLayers[currentLayerId]) {
            sessionLayerData = sessionLayers[currentLayerId];
            console.log('📦 Using cached session layer data:', {
              sessionLayerId: sessionLayerData.id,
              layerId: sessionLayerData.layer_id,
              executionStatus: sessionLayerData.last_execution_status,
              hasMetrics: !!sessionLayerData.last_execution_metrics
            });
          }
          
          if (sessionLayerData) {
            layerData.session_layer = sessionLayerData;
            layerData.last_execution_status = sessionLayerData.last_execution_status;
            layerData.last_execution_metrics = sessionLayerData.last_execution_metrics;
            layerData.last_execution_output = sessionLayerData.last_execution_output;
            layerData.last_execution_at = sessionLayerData.last_execution_at;
          }
          
          console.log('📦 Fetched layer data:', {
            id: layerData.id,
            name: layerData.name,
            type: layerData.type,
            hasFunctionCode: !!layerData.function_code,
            functionCodePreview: layerData.function_code ? layerData.function_code.substring(0, 100) : '(none)',
            hasExecutionData: !!sessionLayerData,
            executionStatus: sessionLayerData?.last_execution_status,
            executionMetrics: sessionLayerData?.last_execution_metrics,
            allKeys: Object.keys(layerData)
          });
          setCurrentLayer(layerData);
          console.log('✅ Layer switch completed:', {
            currentLayerId,
            layerName: layerData.name,
            hasExecutionResults: !!(sessionLayerData?.last_execution_status)
          });
          }
        }
      } catch (error) {
        console.error('Error fetching layer:', error);
        const errorMsg = {
          timestamp: new Date().toLocaleTimeString(),
          type: 'error',
          message: `Failed to load layer: ${error.message}`
        };
        setConsoleOutput(prev => [...prev, errorMsg]);
      } finally {
        setLoading(false);
      }
    };

    fetchLayerDetails();
    // Re-run when:
    // - currentLayerId changes (user selects different layer)
    // - currentSession becomes available (session loads)
    // - sessionLayers populated (cache ready for use)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLayerId, currentSession?.id, Object.keys(sessionLayers || {}).length]);

  // Sync currentLayerId with selectedLayerId prop changes
  useEffect(() => {
    if (selectedLayerId) {
      setCurrentLayerId(selectedLayerId);
    }
  }, [selectedLayerId]);

  // Clear waiting state once session and layer are both loaded
  useEffect(() => {
    if (waitingForSessionData && currentSession && currentLayerId && currentLayer) {
      console.log('Clearing waitingForSessionData - session and layer loaded', {
        sessionId: currentSession.id,
        layerId: currentLayerId,
        hasSessionLayer: !!sessionLayers[currentLayerId]
      });
      setWaitingForSessionData(false);
    }
  }, [waitingForSessionData, currentSession, currentLayerId, currentLayer, sessionLayers]);

  // Update currentLayer when sessionLayers data changes (for real-time execution results)
  // This catches updates from Redis pub/sub when tests complete
  useEffect(() => {
    if (!currentLayer?.id) return; // Skip if no layer loaded yet
    
    const matchingSessionLayer = sessionLayers[currentLayerId];
    
    // Only update if:
    // 1. We have a matching session layer
    // 2. Current layer already has session_layer (means it was previously loaded)
    // 3. Execution status has changed (prevents duplicate updates)
    if (matchingSessionLayer && currentLayer?.session_layer) {
      const statusChanged = matchingSessionLayer.last_execution_status !== currentLayer.last_execution_status;
      
      if (statusChanged) {
        console.log('🔄 Updating currentLayer with fresh execution results:', {
          layerId: currentLayerId,
          layerName: currentLayer?.layer_name || currentLayer?.name,
          oldStatus: currentLayer.last_execution_status,
          newStatus: matchingSessionLayer.last_execution_status,
          hasMetrics: !!matchingSessionLayer.last_execution_metrics
        });
        
        setCurrentLayer(prev => ({
          ...prev,
          session_layer: matchingSessionLayer,
          last_execution_status: matchingSessionLayer.last_execution_status,
          last_execution_metrics: matchingSessionLayer.last_execution_metrics,
          last_execution_output: matchingSessionLayer.last_execution_output,
          last_execution_at: matchingSessionLayer.last_execution_at
        }));
      }
    }
  }, [sessionLayers, currentLayerId, currentLayer]);

  // Auto-close recovery dialog if sessions become empty (shouldn't happen but safeguard)
  useEffect(() => {
    if (showRecoveryDialog && availableSessions.length === 0) {
      console.warn('Recovery dialog open but no sessions available, closing...');
      setShowRecoveryDialog(false);
    }
  }, [showRecoveryDialog, availableSessions]);

  // Execution hooks
  const { submitJob, submitting } = useExecutionSubmit();
  const { job, logs } = useExecutionJob(currentJobId);

  // Show polling feedback every 5 seconds while job is queued
  useEffect(() => {
    if (job?.status === 'queued') {
      const interval = setInterval(() => {
        setPollCount(prev => {
          const newCount = prev + 1;
          if (newCount % 3 === 0) { // Every ~6 seconds (3 polls * 2s)
            const pollMessage = {
              timestamp: new Date().toLocaleTimeString(),
              type: 'info',
              message: `Still waiting for worker... (${newCount * 2}s elapsed)`
            };
            setConsoleOutput(prevOutput => [...prevOutput, pollMessage]);
          }
          return newCount;
        });
      }, 2000);

      return () => clearInterval(interval);
    } else {
      setPollCount(0);
    }
  }, [job?.status]);

  // Auto-scroll console to bottom when output changes
  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [consoleOutput]);

  // Filter function to show only essential pytest output
  const shouldShowLog = (log) => {
    // Always show errors
    if (log.includes('[ERROR]') || log.includes('ERROR') || log.includes('FAILED')) {
      return true;
    }

    // Show test results (PASSED/FAILED)
    if (log.match(/test_.*::.*(PASSED|FAILED)/)) {
      return true;
    }

    // Show final summary line
    if (log.match(/=+.*passed.*=+/) || log.match(/=+.*failed.*=+/)) {
      return true;
    }

    // Show error tracebacks
    if (log.includes('Traceback') || log.includes('Error:') || log.includes('Exception:')) {
      return true;
    }

    // Hide verbose pytest output
    if (log.includes('test session starts') ||
        log.includes('platform linux') ||
        log.includes('platform darwin') ||
        log.includes('platform win32') ||
        log.includes('cachedir:') ||
        log.includes('rootdir:') ||
        log.includes('plugins:') ||
        log.includes('collecting ...') ||
        log.includes('collected') ||
        log.includes('warnings summary') ||
        log.includes('UserWarning') ||
        log.includes('DeprecationWarning') ||
        log.includes('-- Docs:') ||
        log.includes('warnings.warn') ||
        log.match(/^\/.*\.py:\d+/) || // Absolute file paths from warnings
        log.match(/^\.\.\/.*\.py:\d+/) || // Relative file paths from warnings (../)
        log.match(/^\.\/.*\.py:\d+/) || // Relative file paths from warnings (./)
        log.match(/^\s+warnings\.warn/) || // Warning code lines
        log.match(/^={3,}/) && !log.match(/passed|failed/)) { // Separator lines without results
      return false;
    }

    return true;
  };

  // Update console with execution logs (only new ones)
  useEffect(() => {
    if (logs && logs.length > displayedLogsCount) {
      const newLogs = logs.slice(displayedLogsCount);
      const filteredLogs = newLogs.filter(shouldShowLog);
      const executionLogs = filteredLogs.map(log => ({
        timestamp: new Date().toLocaleTimeString(),
        type: log.includes('[ERROR]') || log.includes('FAILED') ? 'error' :
              log.includes('[RESULT]') || log.includes('PASSED') ? 'success' : 'info',
        message: log
      }));
      setConsoleOutput(prev => [...prev, ...executionLogs]);
      setDisplayedLogsCount(logs.length);
    }
  }, [logs, displayedLogsCount]);

  // Update console with job results (ultra-clean - only show final test result)
  useEffect(() => {
    if (job) {
      // Only show final test result when job completes
      if (job.status === 'completed' && lastJobStatusRef.current !== job.status) {
        lastJobStatusRef.current = job.status;
        
        // Debug: Log the full job result
        console.log('🔍 Job result:', job.result);
        console.log('🔍 Test result value:', job.result?.test_result);

        if (job.result?.test_result) {
          const testResultMessage = {
            timestamp: new Date().toLocaleTimeString(),
            type: job.result.test_result === 'PASSED' ? 'success' : 'error',
            message: job.result.test_result  // Just "PASSED" or "FAILED"
          };
          setConsoleOutput(prev => [...prev, testResultMessage]);
        }
      }
      
      // Handle failures and expired jobs (still need error feedback)
      if ((job.status === 'failed' || job.status === 'expired') && lastJobStatusRef.current !== job.status) {
        lastJobStatusRef.current = job.status;
        const errorMessage = {
          timestamp: new Date().toLocaleTimeString(),
          type: 'error',
          message: job.status === 'failed' ? `Job failed: ${job.message || 'Unknown error'}` :
                   'Job expired - no worker available'
        };
        setConsoleOutput(prev => [...prev, errorMessage]);
      }

      // Show metrics if completed (only once)
      if (job.status === 'completed' && !metricsDisplayed) {
        setMetricsDisplayed(true);
        if (job.result?.metrics) {
          const metrics = job.result.metrics;

          // Save execution result to session (check test_result for pass/fail)
          if (currentSession && currentLayer) {
            const executionStatus = job.result.test_result === 'PASSED' ? 'success' : 'failed';
            saveExecutionResult(
              currentLayer.id,
              executionStatus,
              JSON.stringify(job.result, null, 2),
              metrics
            ).catch(err => console.error('Failed to save execution result:', err));
          }
          const metricsMessages = [
            {
              timestamp: new Date().toLocaleTimeString(),
              type: 'success',
              message: `📊 Benchmark Results:`
            },
            {
              timestamp: new Date().toLocaleTimeString(),
              type: 'info',
              message: `   Input Shape: [${metrics.input_shape?.join(', ')}]`
            },
            {
              timestamp: new Date().toLocaleTimeString(),
              type: 'info',
              message: `   Device: ${metrics.device} | dtype: ${metrics.dtype}`
            },
            {
              timestamp: new Date().toLocaleTimeString(),
              type: 'success',
              message: `   Mean Latency: ${metrics.mean_latency_ms}ms`
            },
            {
              timestamp: new Date().toLocaleTimeString(),
              type: 'success',
              message: `   Throughput: ${metrics.throughput_ops_per_sec} ops/sec`
            },
            {
              timestamp: new Date().toLocaleTimeString(),
              type: 'info',
              message: `   Total Time: ${metrics.total_time_sec}s (${metrics.iterations} iterations)`
            }
          ];
          setConsoleOutput(prev => [...prev, ...metricsMessages]);
        }
        
      }

      // Show timeout help message
      if (job.status === 'expired') {
        const helpMessage = {
          timestamp: new Date().toLocaleTimeString(),
          type: 'info',
          message: '💡 Make sure your local worker is running: cd local_worker && python worker.py'
        };
        setConsoleOutput(prev => [...prev, helpMessage]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  // Handler for selecting a different layer from list
  const handleLayerSelect = async (layerId, shouldScroll = false) => {
    if (layerId !== currentLayerId) {
      // Flush any pending autosaves before switching layers
      try {
        await flushAutosave();
      } catch (err) {
        console.error('Failed to save changes before switching layers:', err);
      }
      // Clear execution results when switching layers
      setCurrentJobId(null);
      setCurrentLayerId(layerId);
      
      // Scroll to the selected layer in the list if requested
      if (shouldScroll) {
        setTimeout(() => {
          const layerButton = document.querySelector(`[data-layer-id="${layerId}"]`);
          if (layerButton && layerListRef.current) {
            layerButton.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 100);
      }
    }
  };

  // Handler for graph node selection - navigate to corresponding layer
  const handleGraphNodeSelect = (node) => {
    console.log('Graph node selected:', node);
    
    // Extract fn_name from node attributes (layer execution graph format)
    const fnName = node.attrs?.fn_name || node.fnName || node.name;
    
    console.log('Looking for layer with fn_name:', fnName);
    
    // Try to find matching layer by various name fields
    const matchingLayer = enhancedLayers.find(layer => {
      // Match by layer name (most common for golden functions)
      if (layer.name === fnName) return true;
      
      // Match by function_name field
      if (layer.function_name === fnName) return true;
      
      // Match by layer_name field
      if (layer.layer_name === fnName) return true;
      
      // Also try matching the node's direct name field
      if (layer.name === node.name) return true;
      
      return false;
    });
    
    if (matchingLayer) {
      console.log('Found matching layer:', matchingLayer);
      handleLayerSelect(matchingLayer.id, true); // true = should scroll to center
    } else {
      console.log('No matching layer found for node:', node.name, 'fn_name:', fnName);
      console.log('Available layers:', enhancedLayers.map(l => ({ 
        id: l.id, 
        name: l.name, 
        function_name: l.function_name,
        layer_name: l.layer_name 
      })));
    }
  };

  const generateLayerCode = (layer) => {
    // Handle null/undefined layer
    if (!layer) {
      return '# Loading layer data...';
    }

    // For any layer with function_code (Golden Functions, Composites, etc.), use it
    if (layer.function_code) {
      return layer.function_code;
    }

    // For PyTorch/HF models without function code, return empty stub for future implementation
    return `# Layer optimization editor
#
# Layer: ${layer.name}
# Type: ${layer.type || 'Unknown'}
#
# TODO: Layer optimization functionality will be implemented in a future release.
# This is a placeholder for editing and optimizing individual PyTorch layers.
`;
  };

  const runLocally = async () => {
    if (!currentLayer) return;

    // Flush any pending autosaves before running
    try {
      await flushAutosave();
    } catch (err) {
      console.error('Failed to save changes before running:', err);
      const errorMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Failed to save changes: ${err.message}`
      };
      setConsoleOutput(prev => [...prev, errorMessage]);
      return;
    }

    // Get current code from editor
    const currentCode = editorRef.current?.getValue() || '';

    // Reset counters for new job
    setDisplayedLogsCount(0);
    setMetricsDisplayed(false);
    lastJobStatusRef.current = null;

    // Ultra-clean: No submission messages

    try {
      // Call the specific pytest execution endpoint
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/sessions/${currentSession.id}/layers/${currentLayer.id}/pytest/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ device: 'cpu' })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to execute pytest');
      }

      const result = await response.json();
      setCurrentJobId(result.job_id);

      // Ultra-clean: No queue messages

      // Mark execution as pending in session
      if (currentSession) {
        await saveExecutionResult(currentLayer.id, 'pending', null, null);
      }
    } catch (err) {
      const errorMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Failed to submit job: ${err.message}`
      };
      setConsoleOutput(prev => [...prev, errorMessage]);

      // Save error status to session
      if (currentSession) {
        await saveExecutionResult(currentLayer.id, 'failed', err.message, null);
      }
    }
  };

  const handleTestLayer = async () => {
    if (!currentLayer || !currentSession) return;

    setTestingLayer(true);

    // Reset counters for new job
    setDisplayedLogsCount(0);
    setMetricsDisplayed(false);
    lastJobStatusRef.current = null;

    // Ultra-clean: No submission messages

    try {
      // Call pytest execution endpoint
      const result = await sessionApiService.executePytest(
        currentSession.id,
        currentLayer.id,
        'cpu',
        session.access_token
      );

      setCurrentJobId(result.job_id);

      // Ultra-clean: No queue messages

    } catch (err) {
      const errorMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `❌ Test failed to submit: ${err.message}`
      };
      setConsoleOutput(prev => [...prev, errorMessage]);
    } finally {
      setTestingLayer(false);
    }
  };

  const clearConsole = () => {
    setConsoleOutput([]);
    lastJobStatusRef.current = null;
  };

  const handleSelectSession = async (sessionData) => {
    // Close modal immediately to avoid showing empty state
    setShowRecoveryDialog(false);
    setWaitingForSessionData(true);

    try {
      await loadSession(sessionData.id);
      const recoveryMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'success',
        message: `✅ Loaded session: ${sessionData.session_name}`
      };
      setConsoleOutput(prev => [...prev, recoveryMessage]);
    } catch (err) {
      console.error('Failed to load session:', err);
      setWaitingForSessionData(false);
      const errorMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Failed to load session: ${err.message}`
      };
      setConsoleOutput(prev => [...prev, errorMessage]);
    }
  };

  const handleStartNewSession = async () => {
    setShowRecoveryDialog(false);
    setShowCreateSessionDialog(true);
    // Set default name
    setNewSessionName(`${modelName} - ${new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(',', '')}`);
  };

  const handleCreateSessionWithName = async () => {
    if (!newSessionName.trim()) {
      alert('Please enter a session name');
      return;
    }

    try {
      await createSession(newSessionName.trim());
      setShowCreateSessionDialog(false);
      setNewSessionName('');
      const newSessionMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'info',
        message: '🆕 Started new session'
      };
      setConsoleOutput(prev => [...prev, newSessionMessage]);
    } catch (err) {
      console.error('Failed to start new session:', err);
      const errorMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Failed to start new session: ${err.message}`
      };
      setConsoleOutput(prev => [...prev, errorMessage]);
    }
  };

  const saveOptimization = async () => {
    if (!currentLayer) return;

    // Flush any pending autosaves before saving
    try {
      await flushAutosave();
    } catch (err) {
      console.error('Failed to flush autosave before saving:', err);
    }

    // Get current code directly from editor
    const currentCode = editorRef.current?.getValue();

    const optimizationData = {
      layerId: currentLayer.id,
      layerName: currentLayer.name,
      code: currentCode,
      timestamp: new Date().toISOString(),
      estimatedSpeedup: (Math.random() * 2 + 1).toFixed(2)
    };

    // Add saving message to console
    const savingOutput = {
      timestamp: new Date().toLocaleTimeString(),
      type: 'info',
      message: `Saving optimization for ${currentLayer.name}...`
    };
    setConsoleOutput(prev => [...prev, savingOutput]);

    try {
      const result = await onSaveOptimization(optimizationData);

      // Success message
      const saveOutput = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'success',
        message: `Optimization saved successfully! Version: ${result.version} | Speedup: ${result.speedup}x`
      };
      setConsoleOutput(prev => [...prev, saveOutput]);

      // Re-fetch layer data to get updated function_code
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/api/models/layers/${currentLayerId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const layerData = await response.json();
        setCurrentLayer(layerData);
      }

    } catch (error) {
      // Error message
      const errorOutput = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Failed to save optimization: ${error.message}`
      };
      setConsoleOutput(prev => [...prev, errorOutput]);
    }
  };

  const handleStartSessionRename = () => {
    if (!currentSession) return;
    setRenamingSession(true);
    setSessionRenameValue(currentSession.session_name || '');
  };

  const handleCancelSessionRename = () => {
    setRenamingSession(false);
    setSessionRenameValue('');
  };

  const handleSaveSessionRename = async () => {
    if (!sessionRenameValue.trim() || !currentSession) {
      alert('Session name cannot be empty');
      return;
    }

    try {
      await updateSession({ session_name: sessionRenameValue.trim() });
      setRenamingSession(false);
      setSessionRenameValue('');

      const renameMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'success',
        message: `✓ Session renamed to "${sessionRenameValue.trim()}"`
      };
      setConsoleOutput(prev => [...prev, renameMessage]);
    } catch (err) {
      console.error('Failed to rename session:', err);
      const errorMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Failed to rename session: ${err.message}`
      };
      setConsoleOutput(prev => [...prev, errorMessage]);
    }
  };

  const handleExportPython = async () => {
    if (!currentSession || !session?.access_token) {
      alert('No session available to export');
      console.error('Export failed: currentSession=', currentSession, 'session=', session);
      return;
    }

    // Flush any pending autosaves before exporting
    try {
      await flushAutosave();
    } catch (err) {
      console.error('Failed to flush autosave before exporting:', err);
    }

    setExportingPython(true);

    const exportMessage = {
      timestamp: new Date().toLocaleTimeString(),
      type: 'info',
      message: 'Preparing Python package export...'
    };
    setConsoleOutput(prev => [...prev, exportMessage]);

    try {
      const exportData = await sessionApiService.exportSessionAsPython(
        currentSession.id,
        modelId,
        session.access_token
      );

      await exportSessionToPython(exportData.session, exportData.model, exportData.layers);

      const successMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'success',
        message: `✓ Python package exported successfully`
      };
      setConsoleOutput(prev => [...prev, successMessage]);
    } catch (err) {
      console.error('Failed to export Python package:', err);
      const errorMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Failed to export: ${err.message}`
      };
      setConsoleOutput(prev => [...prev, errorMessage]);
    } finally {
      setExportingPython(false);
    }
  };

  const handleToggleLayerSelection = (layerId) => {
    setSelectedLayers(prev => {
      if (prev.includes(layerId)) {
        return prev.filter(id => id !== layerId);
      } else if (prev.length < 2) {
        return [...prev, layerId];
      } else {
        // Replace the first selected layer with the new one
        return [prev[1], layerId];
      }
    });
  };

  const handleMergeLayers = async () => {
    if (selectedLayers.length !== 2 || !currentSession) {
      return;
    }

    // Flush any pending autosaves before merging
    try {
      await flushAutosave();
    } catch (err) {
      console.error('Failed to flush autosave before merging:', err);
    }

    setMerging(true);
    const [layer1Id, layer2Id] = selectedLayers;

    try {
      const startMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'info',
        message: `🔀 Merging layers...`
      };
      setConsoleOutput(prev => [...prev, startMessage]);

      const result = await sessionApiService.mergeLayers(
        currentSession.id,
        layer1Id,
        layer2Id,
        mergeFeedPositions,
        session.access_token
      );

      const successMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'success',
        message: `✅ Layers merged successfully! New function: ${result.merged_function_name}`
      };
      setConsoleOutput(prev => [...prev, successMessage]);

      // Reload session layers
      await loadSession(currentSession.id);

      // Trigger visualizer refresh
      setVisualizerRefreshTrigger(prev => prev + 1);

      // Reset merge mode
      setMergeMode(false);
      setSelectedLayers([]);
      setShowMergeDialog(false);
      setMergeFeedPositions([0]);

    } catch (err) {
      console.error('Failed to merge layers:', err);
      const errorMessage = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        message: `Failed to merge layers: ${err.message}`
      };
      setConsoleOutput(prev => [...prev, errorMessage]);
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Session Picker Dialog - Now handled in OptimizeHub before navigation */}
      {false && showRecoveryDialog && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-gray-200 flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Choose a Session
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Select an existing session to resume or start a new one
                </p>
              </div>
              <button
                onClick={() => setShowRecoveryDialog(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {availableSessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No sessions found. This shouldn't happen - please try refreshing.</p>
                  <p className="text-xs mt-2">Debug: {JSON.stringify({hasRecoverableSessions, count: availableSessions.length})}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Available Sessions */}
                  {availableSessions.map((sessionItem) => (
                  <button
                    key={sessionItem.id}
                    onClick={() => handleSelectSession(sessionItem)}
                    className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg p-4 border border-gray-200 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {sessionItem.session_name || `Session ${sessionItem.id.slice(0, 8)}`}
                        </h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>
                            Last modified: {sessionItem.last_autosave_at
                              ? new Date(sessionItem.last_autosave_at).toLocaleString()
                              : new Date(sessionItem.updated_at).toLocaleString()}
                          </p>
                          <p>
                            Progress: {sessionItem.optimized_layers_count || 0} / {sessionItem.total_layers_count || 0} layers optimized
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        sessionItem.status === 'active'
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : 'bg-gray-100 text-gray-800 border border-gray-200'
                      }`}>
                        {sessionItem.status}
                      </span>
                    </div>
                  </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={handleStartNewSession}
                disabled={sessionLoading}
                className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sessionLoading ? 'Creating...' : 'Start New Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Session Dialog - Now handled in OptimizeHub before navigation */}
      {false && showCreateSessionDialog && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Create New Session</h3>
              <p className="text-sm text-gray-600 mt-1">
                Give your optimization session a name
              </p>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Name
              </label>
              <input
                type="text"
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateSessionWithName();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter session name..."
                autoFocus
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex space-x-3">
              <button
                onClick={() => {
                  setShowCreateSessionDialog(false);
                  setNewSessionName('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSessionWithName}
                disabled={sessionLoading || !newSessionName.trim()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sessionLoading ? 'Creating...' : 'Create Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      <UnifiedHeader 
        showSearch={false} 
        showUpload={false} 
        showAK47={false}
        modelName={modelName}
        currentSession={currentSession}
        renamingSession={renamingSession}
        sessionRenameValue={sessionRenameValue}
        setSessionRenameValue={setSessionRenameValue}
        onBackToModel={onClose}
        onStartSessionRename={handleStartSessionRename}
        onSaveSessionRename={handleSaveSessionRename}
        onCancelSessionRename={handleCancelSessionRename}
        isLayerModified={isLayerModified(currentLayerId)}
      />

      {/* Main Content - Split View */}
      <div className="flex-1 flex min-h-0 bg-white border-t border-gray-200">
        <PanelGroup direction="horizontal">
          {/* Left Sidebar - Layer List (Resizable) */}
          <Panel defaultSize={25} minSize={15} maxSize={40}>
            <div className="h-full bg-white border-r border-gray-200 flex flex-col overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h2 className="text-sm text-gray-900">Model Layers</h2>
                <div className="flex items-center gap-2">
                  {/* Search Button/Input */}
                  {layerSearchExpanded ? (
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        value={layerSearchQuery}
                        onChange={(e) => setLayerSearchQuery(e.target.value)}
                        placeholder="Search..."
                        autoFocus
                        className="w-32 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      <button
                        onClick={() => {
                          setLayerSearchExpanded(false);
                          setLayerSearchQuery('');
                        }}
                        className="ml-1 p-1 text-gray-400 hover:text-gray-600"
                        title="Close search"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setLayerSearchExpanded(true)}
                      className="p-1.5 rounded-lg transition-colors text-gray-700 bg-gray-50 hover:bg-gray-100"
                      title="Search layers"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setMergeMode(!mergeMode);
                      setSelectedLayers([]);
                    }}
                    className={`p-1.5 rounded-lg transition-colors ${
                      mergeMode
                        ? 'text-purple-700 bg-purple-100 hover:bg-purple-200'
                        : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                    }`}
                    title={mergeMode ? 'Exit merge mode' : 'Enter merge mode'}
                  >
                    <Merge className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {mergeMode && (
                <p className="text-xs text-purple-600 mt-2 font-medium">
                  Select 2 layers to merge ({selectedLayers.length}/2)
                </p>
              )}
              {layerSearchQuery && (
                <p className="text-xs text-blue-600 mt-2 font-medium">
                  {enhancedLayers.filter(layer => {
                    if (layer.name === 'main' || layer.is_helper_functions) return false;
                    const query = layerSearchQuery.toLowerCase();
                    return layer.name.toLowerCase().includes(query) ||
                           (layer.type && layer.type.toLowerCase().includes(query)) ||
                           (layer.layer_name && layer.layer_name.toLowerCase().includes(query));
                  }).length} results
                </p>
              )}
            </div>

            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Pinned main() function */}
              {(() => {
                const mainLayer = enhancedLayers.find(layer => layer.name === 'main');
                if (mainLayer) {
                  const isSelected = currentLayerId === mainLayer.id;
                  return (
                    <div className="flex-shrink-0">
                      <div className="p-3 space-y-1">
                        <button
                          data-layer-id={mainLayer.id}
                          onClick={() => mergeMode ? handleToggleLayerSelection(mainLayer.id) : handleLayerSelect(mainLayer.id)}
                          className={`w-full text-left px-4 py-2.5 rounded-lg transition-all shadow-sm ${
                            selectedLayers.includes(mainLayer.id) 
                              ? 'bg-purple-100 ring-2 ring-purple-500'
                              : isSelected && !mergeMode && mainLayer.last_execution_status === 'success'
                                ? 'bg-gradient-to-r from-green-100/80 to-orange-100/80 ring-2 ring-orange-500 backdrop-blur-md border border-green-200/60'
                                : isSelected && !mergeMode && mainLayer.last_execution_status === 'failed'
                                  ? 'bg-gradient-to-r from-red-100/80 to-orange-100/80 ring-2 ring-orange-500 backdrop-blur-md border border-red-200/60'
                                  : isSelected && !mergeMode
                                    ? 'bg-orange-100 ring-2 ring-orange-500'
                                    : mainLayer.last_execution_status === 'success'
                                      ? 'bg-gradient-to-r from-green-100/80 to-orange-100/80 backdrop-blur-md border border-green-200/60 hover:from-green-100/90 hover:to-orange-100/90'
                                      : mainLayer.last_execution_status === 'failed'
                                        ? 'bg-gradient-to-r from-red-100/80 to-orange-100/80 backdrop-blur-md border border-red-200/60 hover:from-red-100/90 hover:to-orange-100/90'
                                        : 'bg-orange-100/80 backdrop-blur-md border border-orange-200/60 hover:bg-orange-100/90 hover:border-orange-200/80'
                          }`}
                        >
                          <div className="flex items-center">
                            {mergeMode && (
                              <input
                                type="checkbox"
                                checked={selectedLayers.includes(mainLayer.id)}
                                onChange={() => {}}
                                className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-sm text-gray-900 flex items-center">
                                <span className="mr-2">📌</span>
                                {mainLayer.name}
                              </h3>
                              <p className="text-xs text-gray-500 mt-0.5">{mainLayer.type}</p>
                            </div>
                            {isLayerModified(mainLayer.id) && (
                              <div className="ml-2 w-2 h-2 bg-green-500 rounded-full"></div>
                            )}
                          </div>
                        </button>
                        
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Pinned Helper Functions layer */}
              {(() => {
                const helperLayer = enhancedLayers.find(layer => layer.is_helper_functions);
                if (helperLayer) {
                  const isSelected = currentLayerId === helperLayer.id;
                  return (
                    <div className="flex-shrink-0">
                      <div className="p-3 space-y-1">
                        <button
                          data-layer-id={helperLayer.id}
                          onClick={() => handleLayerSelect(helperLayer.id)}
                          className={`w-full text-left px-4 py-2.5 rounded-lg transition-all shadow-sm ${
                            isSelected 
                              ? 'bg-yellow-100 ring-2 ring-yellow-500'
                              : 'bg-yellow-50/80 backdrop-blur-md border border-yellow-200/60 hover:bg-yellow-50/90 hover:border-yellow-200/80'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-sm text-gray-900 flex items-center">
                                <span className="mr-2">🔧</span>
                                {helperLayer.name}
                              </h3>
                              <p className="text-xs text-gray-500 mt-0.5">User-defined utilities</p>
                            </div>
                            {isLayerModified(helperLayer.id) && (
                              <div className="ml-2 w-2 h-2 bg-green-500 rounded-full"></div>
                            )}
                          </div>
                        </button>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Scrollable layer list */}
              <div ref={layerListRef} className="flex-1 overflow-y-auto">
                <div className="p-3 space-y-2">
                  {enhancedLayers
                    .filter(layer => {
                      // Filter out main and helper functions
                      if (layer.name === 'main' || layer.is_helper_functions) return false;
                      
                      // Apply search filter
                      if (layerSearchQuery.trim()) {
                        const query = layerSearchQuery.toLowerCase();
                        return layer.name.toLowerCase().includes(query) ||
                               (layer.type && layer.type.toLowerCase().includes(query)) ||
                               (layer.layer_name && layer.layer_name.toLowerCase().includes(query));
                      }
                      
                      return true;
                    })
                    .map((layer, index) => {
                      const layerIndex = enhancedLayers.findIndex(l => l.id === layer.id);
                      const isSelected = currentLayerId === layer.id;
                      const isJobRunning = job?.status === 'running' || job?.status === 'pending' || job?.status === 'queued';
                      const isDisabled = isJobRunning && !isSelected;
                      
                      // Get test result status for background color
                      const testStatus = layer.last_execution_status;
                      
                      // Debug logging for first layer or selected layer
                      if (index === 0 || isSelected) {
                        console.log(`🎨 Layer gradient debug for "${layer.name}":`, {
                          layerId: layer.id,
                          isSelected,
                          mergeMode,
                          testStatus,
                          last_execution_status: layer.last_execution_status,
                          session_layer: layer.session_layer,
                          full_layer_object: layer
                        });
                      }
                      
                      const getBackgroundClass = () => {
                        // Merge mode selection (purple)
                        if (selectedLayers.includes(layer.id)) {
                          console.log(`🎨 ${layer.name}: Using PURPLE (merge mode selected)`);
                          return 'bg-purple-100 ring-2 ring-purple-500';
                        }
                        
                        // Selected layer with test status gradient + orange ring
                        if (isSelected && !mergeMode) {
                          if (testStatus === 'success') {
                            console.log(`🎨 ${layer.name}: Using GREEN GRADIENT (selected + passed)`);
                            return 'bg-gradient-to-r from-green-50 to-white ring-2 ring-orange-500 shadow-sm';
                          }
                          if (testStatus === 'failed') {
                            console.log(`🎨 ${layer.name}: Using RED GRADIENT (selected + failed)`);
                            return 'bg-gradient-to-r from-red-50 to-white ring-2 ring-orange-500 shadow-sm';
                          }
                          console.log(`🎨 ${layer.name}: Using ORANGE (selected, no test)`);
                          return 'bg-orange-100 ring-2 ring-orange-500 shadow-sm';
                        }
                        
                        // Unselected layer with test status gradient
                        if (testStatus === 'success') {
                          console.log(`🎨 ${layer.name}: Using GREEN GRADIENT (unselected + passed)`);
                          return 'bg-gradient-to-r from-green-50 to-white hover:from-green-100 hover:to-green-50';
                        }
                        if (testStatus === 'failed') {
                          console.log(`🎨 ${layer.name}: Using RED GRADIENT (unselected + failed)`);
                          return 'bg-gradient-to-r from-red-50 to-white hover:from-red-100 hover:to-red-50';
                        }
                        
                        console.log(`🎨 ${layer.name}: Using GRAY (default, no test)`);
                        return 'bg-gray-50 hover:bg-gray-100';
                      };
                      
                      return (
                        <div key={layer.id} className="space-y-1">
                          <button
                            data-layer-id={layer.id}
                            onClick={() => mergeMode ? handleToggleLayerSelection(layer.id) : handleLayerSelect(layer.id)}
                            disabled={isDisabled}
                            className={`w-full text-left px-4 py-2.5 rounded-lg transition-all ${getBackgroundClass()} ${
                              isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            <div className="flex items-center">
                              {mergeMode && (
                                <input
                                  type="checkbox"
                                  checked={selectedLayers.includes(layer.id)}
                                  onChange={() => {}}
                                  className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-sm text-gray-900 flex items-center">
                                  <span className="mr-2 text-xs text-gray-500 font-normal">#{layerIndex}</span>
                                  <span className="truncate">{layer.name}</span>
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">{layer.type}</p>
                              </div>
                              {isLayerModified(layer.id) && (
                                <div className="ml-2 w-2 h-2 bg-green-500 rounded-full"></div>
                              )}
                              {layer.is_composite && (
                                <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                                  Composite
                                </span>
                              )}
                            </div>
                          </button>
                          
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Merge Button - Shows when 2 layers selected */}
            {mergeMode && selectedLayers.length === 2 && (
              <div className="p-3 border-t border-gray-200 bg-purple-50">
                <button
                  onClick={() => setShowMergeDialog(true)}
                  disabled={merging}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  <span>{merging ? 'Merging...' : 'Merge Selected Layers'}</span>
                </button>
              </div>
            )}
            </div>
          </Panel>

          <PanelResizeHandle className="w-1 bg-gray-200 hover:bg-orange-400 transition-colors cursor-col-resize" />

          {/* Main Content Area - Code Editor and Console */}
          <Panel defaultSize={75} minSize={60}>
            <div className="h-full">
          <PanelGroup direction="horizontal">
            {/* Left Panel: Code Editor + Console */}
            <Panel defaultSize={60} minSize={30}>
              <PanelGroup direction="vertical">
                {/* Code Editor */}
                <Panel defaultSize={70} minSize={30}>
                  <div className="h-full bg-white border-r border-gray-200 flex flex-col overflow-hidden">
              <div className="h-10 px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0 flex items-center justify-between">
                    <h2 className="text-sm text-gray-900 flex items-center space-x-2">
                      <Code className="h-4 w-4 text-gray-600" />
                      <span>Code Editor: {currentLayer?.name || 'Loading...'}</span>
                    </h2>
                    <div className="flex items-center space-x-2">
                      {autosaving && (
                        <div className="flex items-center space-x-1.5 text-xs text-blue-600 mr-1">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                          <span>Saving...</span>
                        </div>
                      )}
                      <button
                        onClick={handleExportPython}
                        disabled={exportingPython}
                        className="p-1.5 text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Export as Python package"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        onClick={runLocally}
                        disabled={!currentLayer || submitting || job?.status === 'running'}
                        className="p-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                        title={submitting ? 'Submitting...' : job?.status === 'running' ? 'Running...' : 'Run Benchmark'}
                      >
                        <Play className="h-4 w-4" />
                      </button>
                    </div>
                </div>

              <div className="flex-1 relative min-h-0">
                {loading || !currentLayer || waitingForSessionData ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                      <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                        <p className="mt-2 text-sm text-orange-600">
                          {waitingForSessionData ? 'Loading session data...' : 'Loading layer...'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    (() => {
                      // Session layers are always cloned from model_layers on session creation
                      // So getLayerCode should always return valid code (current_code or baseline_code)
                      const sessionCode = getLayerCode(currentLayer.id);
                      const finalCode = sessionCode || generateLayerCode(currentLayer); // Fallback only if session corrupted
                      console.log('💻 CodeEditor value decision:', {
                        layerId: currentLayer.id,
                        layerName: currentLayer.name,
                        sessionCode: sessionCode ? `${sessionCode.substring(0, 50)}...` : 'null (session may be corrupted)',
                        finalCode: finalCode ? `${finalCode.substring(0, 50)}...` : 'null'
                      });
                      return (
                        <CodeEditor
                          key={`${currentLayer.id}-${currentSession?.id || 'new'}`}
                          value={finalCode}
                          onMount={(editor) => { editorRef.current = editor; }}
                          onContentChange={(code) => {
                            // Trigger autosave on code change
                            if (currentSession && currentLayer) {
                              autosaveLayer(currentLayer.id, code);
                            }
                          }}
                          placeholder="# Write your layer optimization code here..."
                        />
                      );
                    })()
                  )}
                </div>
                  </div>
                </Panel>

                <PanelResizeHandle className="h-1 bg-gray-200 hover:bg-orange-400 transition-colors cursor-row-resize" />

                {/* Execution Results Panel */}
                <Panel defaultSize={30} minSize={20}>
                  <div className="h-full bg-white border-t border-gray-200 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-4">
                      <ExecutionResults 
                        job={job} 
                        layer={currentLayer}
                        functionName={
                          currentLayer?.function_name || 
                          currentLayer?.name || 
                          currentLayer?.layer_name ||
                          (currentLayer?.merge_metadata?.merged_function_name) ||
                          'COMPOSITE_0'
                        }
                        hardware="NVIDIA A100"
                      />
                    </div>
                  </div>
                </Panel>
              </PanelGroup>
            </Panel>

            <PanelResizeHandle className="w-1 bg-gray-200 hover:bg-orange-400 transition-colors cursor-col-resize" />

            {/* Right Panel: Model Visualizer - Full Height */}
            <Panel defaultSize={40} minSize={20}>
              <div className="h-full bg-white flex flex-col overflow-hidden">
            <div className="h-10 px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0 flex items-center justify-between">
              <h2 className="text-sm text-gray-900 flex items-center space-x-2">
                <Eye className="h-4 w-4 text-gray-600" />
                <span>Model Visualizer</span>
              </h2>
            </div>
            <div className="flex-1 relative min-h-0 overflow-hidden">
              <NetronVisualizer
                modelId={modelId}
                sessionId={currentSession?.id}
                refreshTrigger={visualizerRefreshTrigger}
                onNodeSelect={handleGraphNodeSelect}
                height="100%"
              />
            </div>
              </div>
            </Panel>
          </PanelGroup>
            </div>
          </Panel>
        </PanelGroup>
      </div>

        {/* Merge Configuration Dialog */}
        {showMergeDialog && (
            <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-purple-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Configure Layer Merge
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Specify how the layers should be merged
                  </p>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    {/* Explanation */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                      <h4 className="font-medium text-gray-900 mb-2">How Layer Merging Works:</h4>
                      <div className="text-gray-800 space-y-2">
                        <p>1. <strong>Finds patterns:</strong> <code>var0 = func1(...); var1 = func2(var0, ...)</code></p>
                        <p>2. <strong>Creates merged function:</strong> Combines both operations into one</p>
                        <p>3. <strong>Replaces calls:</strong> <code>var1 = Merged_func(...)</code></p>
                        <div className="mt-3 pt-2 border-t border-gray-300">
                          <p><strong>Examples:</strong></p>
                          <p>• <code>[0]</code> → func1 output feeds position 0 of func2</p>
                          <p>• <code>[1]</code> → func1 output feeds position 1 of func2</p>
                          <p>• <code>[0,2]</code> → func1 output feeds positions 0 AND 2 of func2</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Feed to Input Positions
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Specify which input positions of the second layer receive the output from the first layer (comma-separated, 0-indexed)
                      </p>
                      <input
                        type="text"
                        value={mergeFeedPositions.join(', ')}
                        onChange={(e) => {
                          const positions = e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                          setMergeFeedPositions(positions.length > 0 ? positions : [0]);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowMergeDialog(false);
                      setMergeFeedPositions([0]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMergeLayers}
                    disabled={merging}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {merging ? 'Merging...' : 'Merge Layers'}
                  </button>
                </div>
              </div>
            </div>
        )}
    </div>
  );
};

export default LayerOptimizer;
